import {
  EMPTY_ARRAY,
  SecurityTypesUtils,
  useBehaviorSubject,
  useSecuritiesContext,
  type Basket,
  type FundDefinition,
  type MinimalSubscriptionResponse,
  type Security,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../../hooks';
import { useBaskets } from '../../../../providers/BasketsProvider';
import { useFundDefinitions } from '../../../../providers/FundDefinitionProvider';

export interface CurrentBasketDetailsRow {
  rowID: string;
  fundSymbol: string;
  compoundSymbol: string;
  legSymbol: string;
  basketRatioDate?: string;
  basketRatio?: string;
  sizeRatio?: string;
}

export function buildCurrentBasketDetailsRows({
  useFundDefinitionAndTradableIndexesStreams,
  baskets,
  fundsList,
  securitiesBySymbol,
}: {
  useFundDefinitionAndTradableIndexesStreams: boolean;
  baskets: Basket[] | undefined;
  fundsList: FundDefinition[];
  securitiesBySymbol: Map<string, Security>;
}): CurrentBasketDetailsRow[] {
  if (!useFundDefinitionAndTradableIndexesStreams) {
    return (baskets ?? EMPTY_ARRAY).map(basket => ({
      rowID: `${basket.BasketSymbol}-${basket.UnderlyingSecuritySymbol}`,
      fundSymbol: basket.BasketSymbol,
      compoundSymbol: basket.UnderlyingSecuritySymbol,
      legSymbol: basket.UnderlyingSecuritySymbol,
      basketRatioDate: basket.QuantityDetails?.BasketRatioDate,
      basketRatio: basket.QuantityDetails?.BasketRatio,
    }));
  }

  return fundsList.flatMap(fundDefinition => {
    if (!fundDefinition.TradableSecurities) {
      return [];
    }

    return fundDefinition.TradableSecurities.flatMap(tradableSymbol => {
      const tradableSecurity = securitiesBySymbol.get(tradableSymbol);
      if (!SecurityTypesUtils.Compound.isType(tradableSecurity)) {
        return EMPTY_ARRAY;
      }

      return tradableSecurity.Legs.map(leg => ({
        rowID: `${tradableSecurity.Symbol}-${leg.Symbol}`,
        fundSymbol: fundDefinition.Symbol,
        compoundSymbol: tradableSecurity.Symbol,
        legSymbol: leg.Symbol,
        sizeRatio: leg.SizeRatio,
      }));
    });
  });
}

export function useCurrentBasketDetailsRows() {
  const { useFundDefinitionAndTradableIndexesStreams } = useFeatureFlag();
  const { baskets } = useBaskets();
  const { fundsList } = useFundDefinitions();
  const { securitiesBySymbol } = useSecuritiesContext();

  const rows = useMemo(
    () =>
      buildCurrentBasketDetailsRows({
        useFundDefinitionAndTradableIndexesStreams,
        baskets,
        fundsList,
        securitiesBySymbol,
      }),
    [baskets, fundsList, securitiesBySymbol, useFundDefinitionAndTradableIndexesStreams]
  );

  const { observable } = useBehaviorSubject(
    () => ({ initial: true, data: rows }) satisfies MinimalSubscriptionResponse<CurrentBasketDetailsRow>,
    [rows]
  );

  return observable;
}
